import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../api';
import { RNLLoader } from '../UI';
import MRRequestTable from './MRRequestTable';
import MRDetailsView from './MRDetailsView';
import MREditPage from './MREditPage';

function ActionModal({ modal, onClose, onConfirm }) {
  if (!modal) return null;
  const { actionType, row } = modal;
  const isApprove = actionType === 'forward';
  const isReturn = actionType === 'return';
  const title = isApprove ? 'Approve Material Request' : isReturn ? 'Return to Requester' : 'Reject Request';
  const accent = isApprove ? '#16a34a' : isReturn ? '#f97316' : '#ef4444';
  const btnLabel = isApprove ? 'Approve' : isReturn ? 'Return' : 'Reject';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: accent }}>{title}</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          {row?.request_ref} — {isApprove ? 'This will grant final management approval for this material request.' : isReturn ? 'This will return the request to the requester for corrections.' : 'This will permanently reject this material request.'}
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Comment {!isApprove && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <textarea rows={3} value={modal.comment || ''} onChange={e => onConfirm({ ...modal, comment: e.target.value }, true)}
            placeholder={isApprove ? 'Optional note…' : 'Required — explain the reason'}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={modal.loading} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onConfirm(modal, false)} disabled={modal.loading || (!isApprove && !(modal.comment || '').trim())}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: modal.loading ? 0.7 : 1 }}>
            {modal.loading ? 'Processing…' : btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoPending  = () => <svg {...ic}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>;
const IcoApproved = () => <svg {...ic}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
const IcoConsider = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcoHold     = () => <svg {...ic}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>;
const IcoReject   = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
const IcoReturn   = () => <svg {...ic}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>;

// ─── Workspace definitions (6 cards) ─────────────────────────────────────────
const WORKSPACES = [
  {
    id: 'pending_approval',
    ws: 'WORKSPACE 1',
    label: 'PENDING APPROVAL',
    desc: 'Store-confirmed requests awaiting management decision on APP SO Qty and Purchase Qty.',
    statuses: ['store_reviewed', 'pending_approval'],
    accent: '#0891b2',
    bg: '#ecfeff',
    Icon: IcoPending,
  },
  {
    id: 'approved',
    ws: 'WORKSPACE 2',
    label: 'APPROVED',
    desc: 'Requests fully approved by management and forwarded to Procurement.',
    statuses: ['approved'],
    accent: '#16a34a',
    bg: '#f0fdf4',
    Icon: IcoApproved,
  },
  {
    id: 'pending_consideration',
    ws: 'WORKSPACE 3',
    label: 'PENDING CONSIDERATION',
    desc: 'Partially approved requests — quantities split or conditions attached.',
    statuses: ['partially_approved'],
    accent: '#d97706',
    bg: '#fffbeb',
    Icon: IcoConsider,
  },
  {
    id: 'on_hold',
    ws: 'WORKSPACE 4',
    label: 'HOLD',
    desc: 'Requests placed on hold by management pending further review or budget clearance.',
    statuses: ['on_hold'],
    stage: 'mgmt_approval',
    accent: '#4b5563',
    bg: '#f9fafb',
    Icon: IcoHold,
  },
  {
    id: 'rejected',
    ws: 'WORKSPACE 5',
    label: 'REJECTED',
    desc: 'Requests rejected outright by management. View only.',
    statuses: ['rejected'],
    stage: 'mgmt_approval',
    accent: '#dc2626',
    bg: '#fef2f2',
    Icon: IcoReject,
  },
  {
    id: 'returned',
    ws: 'WORKSPACE 6',
    label: 'RETURNED',
    desc: 'Requests returned for corrections before re-submission for approval.',
    statuses: ['returned'],
    stage: 'mgmt_approval',
    accent: '#b45309',
    bg: '#fef3c7',
    Icon: IcoReturn,
  },
];

// ─── Workspace card ───────────────────────────────────────────────────────────
function WorkspaceCard({ ws, active, count, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  const { ws: wsLabel, label, desc, accent, bg, Icon } = ws;
  const isLit = active || hovered;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1,
        minWidth: 0,
        background: (hovered || active) ? bg : '#fff',
        border: active
          ? `3px solid ${accent}`
          : hovered
            ? `2.5px solid ${accent}95`
            : `1.5px solid ${accent}70`,
        borderRadius: 12,
        padding: '16px 18px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: isLit ? `0 6px 20px ${accent}22` : '0 1px 3px rgba(0,0,0,.05)',
        transform: hovered && !active ? 'translateY(-2px)' : 'none',
        transition: 'all .2s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em',
          color: active ? accent : '#9ca3af', textTransform: 'uppercase',
        }}>
          {wsLabel}
        </span>
        <div style={{
          minWidth: 24, height: 24, borderRadius: 50, paddingInline: 6,
          background: accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800,
          boxShadow: `0 2px 6px ${accent}55`,
        }}>
          {count ?? 0}
        </div>
      </div>

      <div style={{ color: accent, marginTop: 2 }}>
        <Icon />
      </div>

      <div style={{
        fontSize: 12.5, fontWeight: 800, color: accent,
        textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3,
      }}>
        {label}
      </div>

      <div style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 }}>
        {desc}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MRMgmtQueue({ onOpen }) {
  const [stats, setStats]               = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeWs, setActiveWs]         = useState(null);
  const [rows, setRows]                 = useState([]);
  const [listLoading, setListLoading]   = useState(false);
  const [viewRow, setViewRow]           = useState(null);
  const [editRow, setEditRow]           = useState(null);
  const [actionModal, setActionModal]   = useState(null);
  const [toast, setToast]               = useState(null);

  useEffect(() => {
    setStatsLoading(true);
    api.getMRMgmtStats()
      .then(setStats)
      .catch(() => setStats({}))
      .finally(() => setStatsLoading(false));
  }, []);

  const loadList = useCallback((ws) => {
    setListLoading(true);
    setRows([]);
    const params = { statuses: ws.statuses.join(','), limit: 50 };
    if (ws.stage) params.stage = ws.stage;
    api.getMRMgmtQueue(params)
      .then(r => setRows(r?.items || []))
      .catch(() => setRows([]))
      .finally(() => setListLoading(false));
  }, []);

  const handleSelect = (ws) => {
    setActiveWs(ws);
    loadList(ws);
  };

  const openAction = (row, actionType) =>
    setActionModal({ row, actionType, comment: '', loading: false });

  const handleActionConfirm = async (modal, commentOnly) => {
    if (commentOnly) { setActionModal(modal); return; }
    const { row, actionType, comment } = modal;
    if ((actionType === 'return' || actionType === 'reject') && !(comment || '').trim()) return;
    setActionModal(m => ({ ...m, loading: true }));
    try {
      if (actionType === 'forward') {
        await api.completeMRMgmtApproval(row.request_id, { comment: comment || undefined });
      } else {
        if (!row.workflow_instance_id) throw new Error('No workflow instance found.');
        await api.actOnWorkflow(row.workflow_instance_id, { action: actionType === 'return' ? 'return' : 'reject', comment });
      }
      setActionModal(null);
      const msg = actionType === 'forward' ? 'Request approved.' : actionType === 'return' ? 'Returned to requester.' : 'Request rejected.';
      setToast({ msg, ok: true });
      setTimeout(() => setToast(null), 3500);
      if (activeWs) loadList(activeWs);
      api.getMRMgmtStats().then(setStats).catch(() => {});
    } catch (err) {
      setActionModal(m => ({ ...m, loading: false }));
      setToast({ msg: err?.message || 'Action failed.', ok: false });
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (statsLoading) {
    return <RNLLoader title="Loading workspaces..." subtitle="Fetching approval stats" />;
  }

  const s = stats || {};

  if (viewRow) {
    return <MRDetailsView row={viewRow} onBack={() => setViewRow(null)} />;
  }
  if (editRow) {
    return <MREditPage row={editRow} onBack={() => setEditRow(null)} onSaved={() => { setEditRow(null); activeWs && loadList(activeWs); }} />;
  }

  const isActionable = activeWs?.id === 'pending_approval';

  return (
    <div>
      <ActionModal
        modal={actionModal}
        onClose={() => setActionModal(null)}
        onConfirm={handleActionConfirm}
      />
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9100, background: toast.ok ? '#16a34a' : '#ef4444', color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
          {toast.msg}
        </div>
      )}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 18, color: 'var(--text-primary)' }}>
          Management Approval
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          Final management sign-off on APP SO Qty (stock-out) and Approved Purchase Qty before Procurement and Site Issue.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {WORKSPACES.map(ws => (
          <WorkspaceCard
            key={ws.id}
            ws={ws}
            count={s[ws.id]}
            active={activeWs?.id === ws.id}
            onClick={() => handleSelect(ws)}
          />
        ))}
      </div>

      <div style={{ minHeight: 220, padding: '8px 0' }}>
        {!activeWs ? (
          <div style={{
            height: 160,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 8, color: '#9ca3af',
          }}>
            <span style={{ fontSize: 22 }}>👆</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              Select a workspace above to load its requests.
            </span>
          </div>
        ) : listLoading ? (
          <RNLLoader title={`Loading ${activeWs.label.toLowerCase()}...`} subtitle="Fetching requests" />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{ width: 4, height: 18, borderRadius: 4, background: activeWs.accent }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: activeWs.accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {activeWs.label}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>— {activeWs.desc}</span>
            </div>
            <MRRequestTable
              rows={rows}
              loading={false}
              onOpen={(id) => setEditRow(rows.find(r => r.request_id === id))}
              onView={(row) => setViewRow(row)}
              onForward={isActionable ? (row) => openAction(row, 'forward') : undefined}
              onReturn={isActionable ? (row) => openAction(row, 'return') : undefined}
              onReject={isActionable ? (row) => openAction(row, 'reject') : undefined}
              forwardLabel="Approve"
              emptyMsg={`No requests in "${activeWs.label}".`}
            />
          </>
        )}
      </div>
    </div>
  );
}
